import { Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';
import { GridComponent, GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { process, State, orderBy } from '@progress/kendo-data-query';
import { ApplicationControlService } from '../../services/applicationcontrol.service';
import { CSEService } from '../../services/cse.service';
import { NotificationService } from '../../services/notification.service';
import { OrderService } from '../../services/order.service';
import { RecordLockService } from '../../services/recordlock.service';
import { UtilService } from '../../services/util.service';
import {
    CarrierAnalysis
    , CarrierLane
    , CarrierLine
    , CarrierList
    , QuoteOrder
    , QuoteOrderLine
    , SearchCarrier
    , SearchQuoteOrderLine
} from '../../models/models';

@Component({
    selector: 'carrier-analysis-grid',
    templateUrl: './carrier-analysis-grid.component.html',
    standalone: false
})
export class CarrierAnalysisGrid implements OnChanges, OnInit {
    @Input() carrierID: number = 0;

    @Output() CarrierEquipmentSaved: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onAddOffer = new EventEmitter<any>();

    @ViewChild(GridComponent, { static: false }) public carrierAnalysisGrid!: GridComponent;
    @ViewChild(GridComponent, { static: false }) public carrierAnalysisMatchGrid!: GridComponent;

    public isEdit: boolean = false;
    public isAdd: boolean = false;

    public daysBackSliderValue: number = 1;
    public daysBack: number = 15;

    public carrierAnalysis: CarrierAnalysis = {} as CarrierAnalysis;
    public carrierLineList: CarrierLine[] = [];
    public isAddOffer: boolean = false;
    public isExcludeNonRMISCertified: boolean = true;
    public isAwardOffer: boolean = false;
    public offerID: number = 0;
    public editedCarrierList: CarrierList = {} as CarrierList;
    private carrier: SearchCarrier = {} as SearchCarrier;
    private offerQuoteOrderLineID: number = 0;
    private offerQuoteOrderLine: QuoteOrderLine = {} as QuoteOrderLine;
    private searchQuoteOrderLine: SearchQuoteOrderLine = {} as SearchQuoteOrderLine;
    private quoteOrder: QuoteOrder = {} as QuoteOrder;

    public recordLockAction: string = "";
    public recordLockSource: string = "Carrier Analysis";
    public recordID: number = 0;
    private handledLoad: CarrierLine = {} as CarrierLine;

    public gridData: GridDataResult = { data: [], total: 0 };
    public gridState: State = { skip: 0, take: 10, sort: [{ dir: 'asc', field: 'originDistributionCenter' }, { dir: 'asc', field: 'destinationDistributionCenter' }] };
    public matchGridData: GridDataResult = { data: [], total: 0 };
    public matchGridState: State = { skip: 0, take: 10, sort: [{ dir: 'asc', field: 'orderNumber' }] };

    constructor(private applicationControlService: ApplicationControlService, private cseService: CSEService,
        private ngZone: NgZone, private notificationService: NotificationService,
        private orderService: OrderService, private recordLockService: RecordLockService, private utilService: UtilService) {
        this.applicationControlService.getApplicationControlsByAppName("IsExcludeNonRMISCertified", false).subscribe(result => {
            if (result.length > 0) {
                var appValue = result[0].appSettingValue;
                if (appValue == "1") {
                    this.isExcludeNonRMISCertified = true;
                }
                else {
                    this.isExcludeNonRMISCertified = false;
                }
            }
        });
    }

    ngOnInit() {
    }

    ngOnChanges() {
        this.getCarrierAnalysis();
    }

    private getCarrierAnalysis() {
        this.utilService.isLoading = true;
        this.cseService.getCarrierAnalysis(this.carrierID, this.daysBack).subscribe(result => {
            this.utilService.isLoading = false;
            //this.carrierLineList = [];
            this.carrierAnalysis = result;
            this.setGrid();
        });
    }

    public sliderTitle(value: number): string {
        var sliderText = '';

        switch (value) {
            case 1:
                sliderText = '15';
                break;
            case 2:
                sliderText = '30';
                break;
            case 3:
                sliderText = '90';
                break;
            case 4:
                sliderText = '120';
                break;
            case 5:
                sliderText = '365';
                break;
        }

        return sliderText;
    }

    public sliderValueChanged() {
        switch (this.daysBackSliderValue) {
            case 1:
                this.daysBack = 15;
                break;
            case 2:
                this.daysBack = 30;
                break;
            case 3:
                this.daysBack = 90;
                break;
            case 4:
                this.daysBack = 120;
                break;
            case 5:
                this.daysBack = 365;
                break;
        }

        this.getCarrierAnalysis();
    }

    private setGrid() {
        if (this.gridState.sort) this.carrierAnalysis.carrierStats = orderBy(this.carrierAnalysis.carrierStats, this.gridState.sort);
        this.gridData = process(this.carrierAnalysis.carrierStats, this.gridState);
        this.fitColumns();
    }

    private fitColumns() {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
            if (this.carrierAnalysisGrid == null) return;
            this.carrierAnalysisGrid.autoFitColumns();
        });
    }

    public dataStateChange(state: DataStateChangeEvent) {
        this.gridState = state;
        this.setGrid();
    }

    private setMatchGrid() {
        if (this.matchGridState.sort) this.carrierLineList = orderBy(this.carrierLineList, this.matchGridState.sort);
        this.matchGridData = process(this.carrierLineList, this.matchGridState);
        console.log("setMatchGrid", this.matchGridData, this.matchGridState);
        this.matchFitColumns();
    }

    private matchFitColumns() {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
            if (this.carrierAnalysisMatchGrid == null) return;
            this.carrierAnalysisMatchGrid.autoFitColumns();
        });
    }

    public matchDataStateChange(state: DataStateChangeEvent) {
        this.matchGridState = state;
        this.setMatchGrid();
    }

    public viewMatches(carrierLaneID: number) {
        this.carrierLineList = this.carrierAnalysis.carrierLines.filter(c => c.carrierLaneID == carrierLaneID);
        this.setMatchGrid();
    }

    public hasMatches(carrierLaneID: number) {
        var matches = this.carrierAnalysis.carrierLines.filter(c => c.carrierLaneID == carrierLaneID);
        if (matches.length > 0) {
            return true;
        }
        else {
            return false;
        }
    }

    public canAddOffer(line: CarrierLine) {
        var ret: boolean = true;
        //if (this.quoteOrderLineList.length > 0 && (load.carrierID == null || load.carrierID == 0)) {
        //    ret = true;
        //}
        return ret;
    }

    public addOffer(line: CarrierLine) {
        this.recordLockAction = "addOffer";
        this.handledLoad = line;
        this.handleRecordLock(line.quoteOrderID);
    }

    private getCarrierOfferFromCarrier() {
        this.editedCarrierList = this.getCarrierListItem();
        this.editedCarrierList.originalQuoteOrderLineID = this.offerQuoteOrderLineID;
        this.editedCarrierList.carrierOfferBatchId = 0;
        this.editedCarrierList.quoteOrderLineId = this.offerQuoteOrderLineID;
        this.editedCarrierList.carrierLaneId = 0;
        this.editedCarrierList.miles = this.offerQuoteOrderLine.miles;
        this.editedCarrierList.offerRate = 0;
        this.editedCarrierList.offerRatePerMile = 0;
        this.editedCarrierList.commentID = 0;
        this.editedCarrierList.commentText = "";
        this.orderService.getSearchQuoteOrderLineByID(this.offerQuoteOrderLineID).subscribe(result => {
            this.searchQuoteOrderLine = result.resultCollection[0];
            this.orderService.getQuoteOrderByID(this.searchQuoteOrderLine.quoteOrderID).subscribe(result2 => {
                this.quoteOrder = result2.resultCollection[0];
            });
        });
        console.log("getCarrierOfferFromCarrier", this.carrierID, this.editedCarrierList);
    }

    public saveQuoteOrderNotes() {
        this.utilService.isLoading = true;
        this.orderService.saveQuoteOrder(this.quoteOrder).subscribe(result => {
            this.utilService.isLoading = false;
            console.log("saveQuoteOrderNotes", result, this.quoteOrder);
        });
    }

    private getCarrierListItem() {
        return {
            quoteOrderLineId: this.offerQuoteOrderLineID,
            carrierId: this.carrier.carrierID,
            carrierCode: this.carrier.carrierCode,
            carrierName: this.carrier.carrierName,
            mcNumber: this.carrier.mcNumber,
            dotNumber: this.carrier.dotNumber,
            scacCode: this.carrier.scacCode,
            originZip3: '',
            destinationZip3: '',
            procurements: 0,
            carrierCity: this.carrier.carrierCity,
            carrierState: this.carrier.carrierState,
            defaultEmail: this.carrier.defaultEmail,
            contactId: this.carrier.contactID,
            modeId: 0,
            trailerTypeId: 0,
            matches: 0,
            miles: this.offerQuoteOrderLine.miles,
            originStateId: 0,
            originMarketId: 0,
            originRegionId: 0,
            destinationStateId: 0,
            destinationMarketId: 0,
            destinationRegionId: 0,
            preferred: false,
            confidenceRanking: 0,
            source: '',
            ratePerMile: 0,
            lastRan: new Date(),
            offerId: 0,
            carrierLaneId: 0,
            lastOfferRate: 0,
            lastOfferDate: new Date(),
            offerRate: 0,
            offerRatePerMile: 0,
            minRate: 0,
            minRateDate: new Date(),
            carrierStatusRowColor: this.carrier.carrierStatusRowColor,
            extendedRate: 0,
            emailed: false,
            declined: false,
            contacted: false,
            awarded: false,
            commentID: 0,
            commentText: "",
            createdByEmployeeID: 0,
            carrierTeam: this.carrier.carrierTeam,
            rmisCertifiedColor: this.carrier.rmisCertifiedColor,
            rmisPotentialCarrierColor: this.carrier.rmisPotentialCarrierColor,
            rmisStatus: this.carrier.rmisStatus,
            nonCertifiedReasons: this.carrier.nonCertifiedReasons,
            rmisStatusColor: this.carrier.rmisStatusColor,
            rmisStatusTextColor: this.carrier.rmisStatusTextColor,
            rmisStatusAbbrev: this.carrier.rmisStatusAbbrev,
            rmisLastPotentialDate: this.carrier.rmisLastPotentialDate,
            isRMISCertified: this.carrier.isRMISCertified,
            carrierIncidents: this.carrier.carrierIncidents
        } as CarrierList;
    }

    public calculateOfferRate(offerRatePerMile: number) {
        if (this.offerQuoteOrderLine.miles > 0) {
            return parseFloat((offerRatePerMile * this.offerQuoteOrderLine.miles).toFixed(2));
        }
        //console.log("calculateRate", offerRatePerMile, this.quoteOrderLine.miles);
    }

    public calculateOfferRatePerMile(offerRate: number) {
        if (this.offerQuoteOrderLine.miles > 0) {
            return parseFloat((offerRate / this.offerQuoteOrderLine.miles).toFixed(2));
        }
        //console.log("calculateRate", offerRate, this.quoteOrderLine.miles);
    }

    public cancelOffer() {
        //this.cancelHandler({ sender: this.carriersGrid, rowIndex: this.editedRowIndex });
        this.offerQuoteOrderLineID = 0;
        this.carrier = {} as SearchCarrier;
        this.editedCarrierList = {} as CarrierList;
        this.offerQuoteOrderLine = {} as QuoteOrderLine;
        this.searchQuoteOrderLine = {} as SearchQuoteOrderLine;
        this.quoteOrder = {} as QuoteOrder;
        this.isAddOffer = false;
    }

    public saveOffer(isAwardOffer: boolean) {
        this.utilService.isLoading = true;
        this.isAwardOffer = isAwardOffer;
        //console.log("saveOffer", this.editedCarrierList);
        if (this.editedCarrierList!.carrierLaneId == 0 || this.editedCarrierList!.carrierLaneId == null) {
            this.saveNewCarrierLaneOffer();
        }
        else {
            this.saveCarrierFromGrid(this.editedCarrierList!, true);
            //this.editedCarrierList = undefined;
            //this.onCarrierClick.emit(0);
        }
        console.log("order-form:saveOffer", this.offerQuoteOrderLineID, this.editedCarrierList);
    }

    public saveNewCarrierLaneOffer() {
        console.log("saveNewCarrierLaneOffer", this.offerQuoteOrderLineID, this.editedCarrierList, this.offerQuoteOrderLine);
        this.utilService.isLoading = true;
        this.editedCarrierList!.carrierOfferBatchId = 0;
        this.cseService.getCarrierLaneByCarrierIDGeocodeLaneID(this.editedCarrierList!.carrierId, this.offerQuoteOrderLine.geocodeLaneID, this.offerQuoteOrderLineID).subscribe(result => {
            //console.log("carrierLane", result);
            this.utilService.isLoading = false;
            var lane: CarrierLane = result.resultCollection[0];
            if (lane.procurements == 0) {
                this.editedCarrierList!.source = "ZipToZip";
            }
            this.editedCarrierList!.carrierLaneId = lane.carrierLaneID;
            this.editedCarrierList!.createdByEmployeeID = parseInt(localStorage.getItem('EmployeeId')!);

            //if (this.editedCarrierList!.emailed == true) {
            //    // Emailed selected, so pop up email form an d include default carrier email
            //    this.sendCarrierEmail(this.editedCarrierList!, false);
            //}
            this.utilService.isLoading = true;
            //console.log("saveOfferFromCarrierList", this.editedCarrierList!);
            this.cseService.saveOfferFromCarrierList(this.editedCarrierList!).subscribe(result2 => {
                this.utilService.isLoading = false;
                if (this.isExcludeNonRMISCertified && !this.editedCarrierList!.isRMISCertified && !this.searchQuoteOrderLine.isMexicoOnly && !this.searchQuoteOrderLine.isExcludeRMISCertification) {
                    // Carrier not RMIS certified and app control flag set
                    this.notificationService.showWarning("Carrier is not RMIS certified. Award not available.", "WARNING: Not RMIS Certified");
                }
                else if (this.isAwardOffer) {
                    this.offerID = result2.resultCollection[0].offerId;
                }
                else {
                    this.offerID = 0;
                }
                if (result2.resultCollection[0].offerId == 0) {
                    // Offer already exists
                    this.notificationService.showWarning("An offer already exists for this carrier lane.", "WARNING: Offer Exists");
                }
                else {
                    this.editedCarrierList!.offerId = result2.resultCollection[0].offerId;
                    this.notificationService.showSuccess("Offer succesfully saved.", "SUCCESS: Offer Saved");
                }

                this.offerQuoteOrderLineID = 0;
                this.carrier = {} as SearchCarrier;
                this.editedCarrierList = {} as CarrierList;
                this.offerQuoteOrderLine = {} as QuoteOrderLine;
                this.searchQuoteOrderLine = {} as SearchQuoteOrderLine;
                this.quoteOrder = {} as QuoteOrder;
                this.isAddOffer = false;
            }, error => {
                var ok = error;
                this.utilService.isLoading = false;
            });
        });
    }

    private saveCarrierFromGrid(carrier: CarrierList, isSaveAndEmail: boolean) {
        carrier.originalQuoteOrderLineID = this.offerQuoteOrderLineID;
        console.log("saveCarrierFromGrid", this.offerQuoteOrderLineID, carrier);
        if (carrier.createdByEmployeeID == null || carrier.createdByEmployeeID == 0) {
            carrier.createdByEmployeeID = parseInt(localStorage.getItem('EmployeeId')!);
        }
        //if (isSaveAndEmail && carrier.emailed == true) {
        //    // Emailed selected, so pop up email form and include default carrier email
        //    this.sendCarrierEmail(carrier, false);
        //}
        this.cseService.saveOfferFromCarrierList(carrier as CarrierList).subscribe(result => {
            this.utilService.isLoading = false;
            if (this.isExcludeNonRMISCertified && !carrier.isRMISCertified && !this.searchQuoteOrderLine.isMexicoOnly && !this.searchQuoteOrderLine.isExcludeRMISCertification) {
                // Carrier not RMIS certified and app control flag set
                this.notificationService.showWarning("Carrier is not RMIS certified. Award not available.", "WARNING: Not RMIS Certified");
            }
            else if (this.isAwardOffer) {
                this.offerID = result.resultCollection[0].offerId;
            }
            else {
                this.offerID = 0;
            }
            if (result.resultCollection[0].offerId == 0) {
                // Offer already exists
                this.notificationService.showWarning("An offer already exists for this carrier lane.", "WARNING: Offer Exists");
            }
            else {
                carrier.offerId = result.resultCollection[0].offerId;
                this.notificationService.showSuccess("Offer succesfully saved.", "SUCCESS: Offer Saved");
            }
            this.offerQuoteOrderLineID = 0;
            this.carrier = {} as SearchCarrier;
            this.editedCarrierList = {} as CarrierList;
            this.offerQuoteOrderLine = {} as QuoteOrderLine;
            this.searchQuoteOrderLine = {} as SearchQuoteOrderLine;
            this.quoteOrder = {} as QuoteOrder;
            this.isAddOffer = false;
            console.log("saveCarrierFromGrid", this.offerQuoteOrderLineID, result);
        }, error => {
            var ok = error;
            this.utilService.isLoading = false;
        });
    }

    public resetAwardOffer() {
        this.offerID = 0;
        this.offerQuoteOrderLineID = 0;
        this.carrier = {} as SearchCarrier;
        this.editedCarrierList = {} as CarrierList;
        this.offerQuoteOrderLine = {} as QuoteOrderLine;
        this.searchQuoteOrderLine = {} as SearchQuoteOrderLine;
        this.quoteOrder = {} as QuoteOrder;
        this.isAddOffer = false;
    }

    public edit(carrierEquipmentID: number) {
        //this.carrierEquipmentID = carrierEquipmentID;
        this.isEdit = true;
        this.isAdd = false;
    }

    public addNew() {
        //this.carrierEquipmentID = 0;
        this.isEdit = false;
        this.isAdd = true;
    }

    public cancelEdit() {
        this.isEdit = false;
        this.isAdd = false;
        if (this.recordID > 0 && !this.recordLockService.isLocked) {
            this.recordLockService.clearRecordLock("QuoteOrder", this.recordID, this.recordLockSource);
            //this.recordLockService.clearRecordLock(this.recordLockService.recordLock.recordLockID);
            this.handledLoad = {} as CarrierLine;
            this.recordID = 0;
        }
    }

    public refreshGrid() {
        this.cancelEdit();
        this.getCarrierAnalysis();
    }

    private handleRecordLock(id: number) {
        this.recordID = id;
        this.recordLockService.handleRecordLock("QuoteOrder", id, this.recordLockSource);
        this.recordLockService.continueRecordLockObserver.subscribe(result => {
            //console.log("order-stop-grid:handleRecordLock:isContinueRecorLock", this.recordLockService.isContinueRecordLock);
            if (this.recordLockService.isContinueRecordLock) {
                this.continueRecordLockAction();
            }
        });
    }

    private continueRecordLockAction() {
        if (!this.recordLockService.isLocked) {
            if (this.recordLockAction == "addOffer") {
                //this.onAddOffer.emit(this.handledLoad.quoteOrderLineID);
                this.offerQuoteOrderLineID = this.handledLoad.quoteOrderLineID;
                this.orderService.getQuoteOrderLineById(this.handledLoad.quoteOrderLineID).subscribe(result => {
                    this.offerQuoteOrderLine = result;
                    this.handledLoad = {} as CarrierLine;
                    this.recordID = 0;
                    this.cseService.getSearchCarrierByCarrierID(this.carrierID).subscribe(result2 => {
                        this.carrier = result2.resultCollection[0];
                        this.getCarrierOfferFromCarrier();
                        this.isAddOffer = true;
                    });
                });
            }
            this.recordLockAction = "";
        }
    }
}
